import { colorwayEmbeddingKey } from '@apps/card/utils/colorway';

/**
 * Fallback theme mapping for "Shop Matching Designs".
 *
 * When a design does not have its own template for a given category (e.g.
 * enclosure cards), the PDP uses this map to surface related designs from
 * other theme families.
 *
 * Shape: source themeId -> array of fallback themeIds.
 * Unidirectional: an entry for gala -> ['doodle'] does NOT imply
 * doodle -> ['gala'] unless explicitly added.
 *
 * The fallback themeIds are queried as-is. For legacy templates that bake a
 * color variant into the themeId (e.g. `doodle_green`), use
 * `FALLBACK_COLORWAY_MAP` to translate the source design's colorway to the
 * fallback theme's color suffix. The legacy themeId resolution in
 * `getRelatedThemeIds` below then builds `doodle_green` automatically.
 *
 * To add a new fallback relationship:
 * 1. Add `['sourceThemeId', ['fallbackThemeId1', 'fallbackThemeId2']]`
 * 2. If the fallback theme uses different color names, add a colorway
 *    translation entry in `FALLBACK_COLORWAY_MAP`.
 */
export const FALLBACK_THEMES: ReadonlyMap<string, readonly string[]> = new Map([
  // Gala invitation -> Doodle enclosure cards
  ['gala', ['doodle']],

  // La Luce invitation -> Doodle enclosure cards
  ['la_luce', ['doodle']],

  // Bonsoir invitation -> Doodle enclosure cards
  ['bonsoir', ['doodle']],

  // Quill invitation -> The Night Before, Weekend Events, The Hotel enclosure cards
  ['quill', ['the_night_before', 'weekend_events', 'the_hotel']],
]);

/**
 * Composes a `FALLBACK_COLORWAY_MAP` key from the source theme and colorway.
 * The colorway is normalized to lowercase snake_case so display-cased values
 * (e.g. `Rose Gold`) match the entries below.
 */
export const fallbackColorwayKey = (themeId: string, colorwayId: string): string => `${themeId}+${colorwayId.toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;

/**
 * Colorway translation for cross-theme fallbacks.
 *
 * When the source theme's colorway name differs from the fallback theme's
 * color variant, this map provides the translation so the correct legacy
 * color-suffixed themeId is queried.
 *
 * Key: `{sourceThemeId}+{sourceColorway}` (composed with
 * `fallbackColorwayKey`). The `+` separator keeps the two parts unambiguous —
 * themeIds themselves contain underscores (`la_luce`, `the_night_before`), so
 * an underscore-joined key could not be read back apart.
 *
 * Only entries where the names differ need to be listed. When a colorway is
 * not in this map, the source colorway name is used as-is (identity mapping).
 */
export const FALLBACK_COLORWAY_MAP: ReadonlyMap<string, string> = new Map([
  // Gala -> Doodle color translations
  ['gala+tomato', 'red'],
  ['gala+olive', 'green'],

  // Bonsoir -> Doodle color translations
  ['bonsoir+pink', 'magenta'],
  ['bonsoir+orange', 'red'],

  // Quill -> The Night Before / Weekend Events / The Hotel color translations
  ['quill+blue', 'lilac'],
  ['quill+chartreuse', 'cream'],
  ['quill+denim', ''], // maps to base (no suffix)
  ['quill+cherry', 'olive'],
  ['quill+burgundy', 'cream'],
  ['quill+red', 'teal'],
  ['quill+green', 'olive'],
]);
export type RelatedThemeIds = Readonly<{
  /** The current design's themeId plus its legacy color-suffixed variants. */
  primary: readonly string[];
  /** Fallback-family themeIds, used only for categories `primary` does not cover. */
  fallback: readonly string[];
}>;

/**
 * Builds the themeIds to query for related items:
 * 1. The current themeId (always)
 * 2. `{themeId}_{colorwayId}` for legacy templates whose color variant is baked
 *    into the themeId (e.g. `gala_black` instead of `gala` + colorway `black`)
 * 3. Fallback themes, if configured for this themeId, plus their legacy
 *    color-suffixed variants (with optional colorway translation)
 *
 * (1) and (2) are returned as `primary`, (3) as `fallback`, so callers can
 * prefer the base family per category and only fill gaps with the fallback.
 */
export const getRelatedThemeIds = (themeId: string, selectedColorwayId?: string): RelatedThemeIds => {
  const primary = new Set<string>([themeId]);

  // Legacy color-suffixed themeIds for the current design. Legacy themeIds are
  // lowercase snake_case, so normalize the colorway the same way the
  // embeddings index does (e.g. `Rose Gold` -> `rose_gold`).
  if (selectedColorwayId) {
    primary.add(colorwayEmbeddingKey(themeId, selectedColorwayId));
  }

  // Fallback themes (unidirectional)
  const fallback = new Set<string>();
  const fallbacks = FALLBACK_THEMES.get(themeId);
  if (fallbacks) {
    for (const fallbackThemeId of fallbacks) {
      fallback.add(fallbackThemeId);

      // Build legacy color-suffixed themeId for the fallback theme, translating
      // the colorway name when the fallback uses different color names.
      if (selectedColorwayId) {
        const translatedColorway = FALLBACK_COLORWAY_MAP.get(fallbackColorwayKey(themeId, selectedColorwayId)) ?? selectedColorwayId;
        if (translatedColorway) {
          fallback.add(colorwayEmbeddingKey(fallbackThemeId, translatedColorway));
        }
      }
    }
  }

  return { primary: Array.from(primary), fallback: Array.from(fallback) };
};
